/**
 * `/notifications` - EVERYTHING THE PLATFORM HAS TOLD THE AUTHOR: `listNotifications`,
 * `markNotificationsRead`.
 *
 * Register R-015 (lens findings U1-03, C4-02). Four served operations had no caller and the cabinet
 * had no feed, no read marks and no preferences - while every monthly statement e-mail links to
 * `/notifications/preferences` (./NotificationPreferencesRoute.tsx).
 *
 * WHAT THE FEED IS. A row for EVERY author-audience category, including ones whose channel the
 * author switched off: those rows are `suppressed`, and they are here on purpose - "a preference
 * means 'do not buzz me', not 'do not record it'". Newest first, keyset-paged.
 *
 * ★ NO COUNT, ANYWHERE. The page carries no `total` by rule (an append-heavy stream), so there is no
 * "3 unread" and no sidebar badge (the shell's attention rule, `features/shell/cabinet-attention.ts`).
 * Unread rows are marked on the row itself.
 *
 * ★ "MARK ALL AS READ" SENDS A ROW'S OWN INSTANT. Read state moves by a cut-off, not per row - a row
 * has no public id - and the cut-off is the `createdAt` of the newest row on screen, so the client
 * reads no clock (authors-api.yaml, `markNotificationsRead`). Anything produced after the page
 * loaded is newer than that and stays unread, which is exactly right: the author has not seen it.
 *
 * ★ THE PAGE ALSO READS THE AUTHOR'S SWITCHES (`getNotificationPreferences`), AND ONLY TO WORD ONE
 * STATE (design 14, F9). A `suppressed` row is "you switched these off" only for a category the
 * author can switch and did; any other `suppressed` row went nowhere for want of an address, and
 * says so (`stateWords`, ./notification-words.ts). Which categories are switchable is the matrix's
 * own `isAlwaysOn`, the same answer the preferences page draws its two lists from - never a list
 * typed into this app. The table waits for that read to SETTLE, so a row's words never change under
 * the reader's eye; if it fails, the feed is still shown and a suppressed row says only "Not sent".
 *
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01). The prototype does not draw this
 * screen; it is drawn in its language: the header's link as an outline pill, the All / Unread filter
 * as the kit's `PillChoice` (still a request to the platform, `unreadOnly`), "Mark all as read"
 * beside it, and the feed as one bordered card of ruled rows with the prototype's dot - gold for a
 * row not yet read - and a small gold "Unread". Still a real table with its caption (now visually
 * hidden), because the accessibility leg finds the feed by that name and a row's cell by its words.
 * "The platform" is "MoonX" in what an author reads. Every read, write and state is as it was.
 */
import { Button, Group, Stack, Table, Text, VisuallyHidden } from '@mantine/core';
import { Check, SlidersHorizontal } from '@moonx/ui';
import { useCallback, useState } from 'react';
import { useCabinetApi } from '../../features/cabinet/api';
import type { OperationResult } from '../../features/cabinet/api';
import {
  CabinetPageHeader,
  EmptyState,
  ErrorState,
  LoadMore,
  LoadingState,
  Money,
  PillChoice,
  RefreshingNote,
} from '../../features/cabinet/components';
import { useAction, useAsync, useKeysetList } from '../../features/cabinet/state';
import { formatInstant, formatPeriod } from '../../features/cabinet/format';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { categoryWords, deliveryWords, stateWords } from './notification-words';
import type { PreferenceFacts } from './notification-words';
import { NOTIFICATION_PREFERENCES_PATH } from './paths';
import { useTranslation } from '../../i18n';
import classes from './notifications.module.css';

type NotificationPage = OperationResult<'listNotifications'>;
type NotificationRow = NonNullable<NotificationPage['items']>[number];

function isUnread(row: NotificationRow): boolean {
  return row.readAt === undefined || row.readAt === null;
}

export function NotificationsRoute() {
  const { t } = useTranslation('engagement');
  const api = useCabinetApi();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [marked, setMarked] = useState<number | null>(null);

  const read = useCallback(
    (cursor: string | undefined, signal: AbortSignal) =>
      api.listNotifications(
        {
          ...(unreadOnly ? { unreadOnly: true } : {}),
          ...(cursor === undefined ? {} : { cursor }),
        },
        signal,
      ),
    [api, unreadOnly],
  );
  const list = useKeysetList<NotificationRow, NotificationPage>(
    read,
    `notifications:${unreadOnly ? 'unread' : 'all'}`,
  );

  // The author's switches, read only to word a `suppressed` row (see the header). `undefined` once
  // the read has failed, which `stateWords` answers with the one claim the state alone proves.
  const readPreferences = useCallback(
    (signal: AbortSignal) => api.getNotificationPreferences(signal),
    [api],
  );
  const switches = useAsync(readPreferences, 'notifications:feed-switches');
  const switchesSettled = switches.status !== 'loading' || switches.data !== undefined;
  const preferences: readonly PreferenceFacts[] | undefined = switches.data?.items;

  const markRead = useAction((upTo: string) => api.markNotificationsRead({ upTo }));
  const newest = list.items[0];
  const anyUnread = list.items.some(isUnread);

  const onMarkAll = () => {
    if (newest === undefined) {
      return;
    }
    void markRead.run(newest.createdAt).then((result) => {
      if (result !== undefined) {
        setMarked(result.markedRead);
        list.reload();
      }
    });
  };

  return (
    <Stack gap="lg">
      <CabinetPageHeader
        title={t('notifications.feed.title')}
        lede={t('notifications.feed.lede')}
        actions={
          <InAppAnchor
            href={NOTIFICATION_PREFERENCES_PATH}
            className={classes.pillLink}
            c="oklch(var(--mx-text))"
            underline="never"
          >
            <SlidersHorizontal size={14} className={classes.pillIcon} aria-hidden />
            {t('notifications.feed.choose')}
          </InAppAnchor>
        }
      />

      <div className={classes.toolbar}>
        <PillChoice
          label={t('notifications.feed.filter.label')}
          value={unreadOnly ? 'unread' : 'all'}
          onChange={(value) => {
            setMarked(null);
            setUnreadOnly(value === 'unread');
          }}
          options={[
            { value: 'all', label: t('notifications.feed.filter.all') },
            { value: 'unread', label: t('notifications.feed.filter.unread') },
          ]}
        />
        <Button
          variant="default"
          radius="xl"
          size="sm"
          leftSection={<Check size={14} aria-hidden />}
          onClick={onMarkAll}
          loading={markRead.isRunning}
          disabled={!anyUnread}
        >
          {t('notifications.feed.markAll')}
        </Button>
      </div>

      {marked === null ? null : (
        <Text size="sm" role="status">
          {marked === 1
            ? t('notifications.feed.markedOne')
            : t('notifications.feed.marked', { count: marked })}
        </Text>
      )}
      {markRead.error === undefined ? null : (
        <ErrorState title={t('notifications.feed.markFailed')} error={markRead.error} />
      )}

      {list.isInitialLoad || !switchesSettled ? (
        <LoadingState label={t('notifications.feed.reading')} rows={4} />
      ) : list.status === 'error' && list.items.length === 0 ? (
        <ErrorState
          title={t('notifications.feed.failed')}
          error={list.error}
          onRetry={list.reload}
        />
      ) : list.items.length === 0 ? (
        <EmptyState
          headline={
            unreadOnly
              ? t('notifications.feed.empty.unreadHeadline')
              : t('notifications.feed.empty.noneHeadline')
          }
          explanation={
            unreadOnly
              ? t('notifications.feed.empty.unreadExplanation')
              : t('notifications.feed.empty.noneExplanation')
          }
        />
      ) : (
        <Stack gap="sm">
          {list.status === 'error' ? (
            <ErrorState
              title={t('notifications.feed.lastReadFailed')}
              error={list.error}
              onRetry={list.reload}
            />
          ) : null}
          {list.isLoadingMore ? (
            <RefreshingNote label={t('notifications.feed.loadingMore')} />
          ) : null}
          <div className={classes.listCard}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {/* The table's name; the page header already says it to the eye. */}
              <VisuallyHidden component="caption">{t('notifications.feed.caption')}</VisuallyHidden>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th scope="col" className={classes.th}>
                    {t('notifications.feed.columns.what')}
                  </Table.Th>
                  <Table.Th scope="col" className={classes.th}>
                    {t('notifications.feed.columns.when')}
                  </Table.Th>
                  <Table.Th scope="col" className={classes.th}>
                    {t('notifications.feed.columns.how')}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {/* NO ID TO KEY ON: a row carries none (NotificationPage). Its instant and its
                    category together are what the platform itself distinguishes them by here. */}
                {list.items.map((row, index) => (
                  <NotificationRowView
                    key={`${row.createdAt}#${row.categoryCode}#${String(index)}`}
                    row={row}
                    preferences={preferences}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </div>
          <LoadMore
            hasMore={list.hasMore}
            isLoading={list.isLoadingMore}
            noun={t('notifications.feed.noun')}
            count={list.items.length}
            onLoadMore={list.loadMore}
          />
        </Stack>
      )}
    </Stack>
  );
}

function NotificationRowView({
  row,
  preferences,
}: {
  readonly row: NotificationRow;
  readonly preferences: readonly PreferenceFacts[] | undefined;
}) {
  const { t } = useTranslation('engagement');
  const unread = isUnread(row);
  const references = row.references;
  const deliveries = row.deliveries ?? [];

  return (
    <Table.Tr>
      <Table.Td>
        <div className={classes.what}>
          {/* The dot is for the eye; the words after the title say "Unread" for everyone. */}
          <span
            className={`${classes.dot ?? ''} ${unread ? (classes.dotUnread ?? '') : ''}`}
            aria-hidden="true"
          />
          <Stack gap={2}>
            <Group gap={8}>
              <Text size="sm" fw={unread ? 700 : 400}>
                {categoryWords(row.categoryCode)}
              </Text>
              {unread ? (
                <span className={classes.unread}>{t('notifications.feed.unread')}</span>
              ) : null}
            </Group>
            {references?.articleTitle === undefined || references.articleTitle === null ? null : (
              <Text size="xs" c="dimmed">
                {references.articleTitle}
              </Text>
            )}
            {references?.statementPeriod === undefined ||
            references.statementPeriod === null ? null : (
              <Text size="xs" c="dimmed">
                {formatPeriod(references.statementPeriod)}
              </Text>
            )}
            {references?.amountMinor === undefined ||
            references.amountMinor === null ||
            references.amountCurrency === undefined ||
            references.amountCurrency === null ? null : (
              <Text size="xs" c="dimmed">
                <Money minor={references.amountMinor} currency={references.amountCurrency} />
              </Text>
            )}
          </Stack>
        </div>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed" className={classes.when}>
          {formatInstant(row.createdAt)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Stack gap={2}>
          <Text size="xs">{stateWords(row.state, row.categoryCode, preferences)}</Text>
          {deliveries.map((delivery, index) => (
            <Text key={`${delivery.channel}#${String(index)}`} size="xs" c="dimmed">
              {deliveryWords(delivery)}
            </Text>
          ))}
        </Stack>
      </Table.Td>
    </Table.Tr>
  );
}
